import { textWithoutSan } from "./memoryCue.ts";
import { buildHintLadder } from "@js/coaching.js";
import type { LessonCourse, LessonNode } from "../lessons/schema.ts";
import type { Chess } from "@vendor/chess.js";
import { reviewedContinuationAtPosition } from "./reviewedContinuations.ts";
import { bookCoachingAtPosition } from "./bookCoaching.ts";
import { positionGuideFor } from "./positionCoaching.ts";
import { authoredObjective, teachingMoveFor } from "../lessons/repository.ts";

export const HINT_STEP_IDS = ["goal", "piece", "candidates", "move", "explanation"] as const;
export type HintStepId = (typeof HINT_STEP_IDS)[number];
export type HintDepth = 0 | 1 | 2 | 3 | 4 | 5;

export type TutorHintStep = {
  id: HintStepId;
  label: string;
  text: string;
  revealsAnswer?: boolean;
};

export const HINT_STEP_LABELS: Record<HintStepId, string> = {
  goal: "Goal",
  piece: "Piece / area",
  candidates: "Candidates",
  move: "Move",
  explanation: "Explanation",
};

const BOLD_SAN = /\*\*([^*]+)\*\*/;

function splitMoveAndWhy(last: string): { move: string; explanation: string } {
  const match = last.match(BOLD_SAN);
  if (!match) {
    return {
      move: last,
      explanation: "Play the move that best serves this position's plan, then read the coach.",
    };
  }
  const san = match[1];
  const leftover = last
    .replace(BOLD_SAN, "")
    .replace(/^(The main line here is|The best move is|The engine's top pick is)\s*/i, "")
    .replace(/^[—–\-\s,]+/, "")
    .replace(/^:\s*/, "")
    .trim();
  return {
    move: `The move to play is **${san}**.`,
    explanation: leftover || `This is the course move in this position: **${san}**.`,
  };
}

function rawTutorHintLadder(ctx: {
  ply: number;
  opening: unknown;
  chess: Chess;
  engineBestSan?: string | null;
  bookSan?: string | null;
  candidateSans?: string[];
  node?: LessonNode | null;
  course?: LessonCourse | null;
  /** The sample line the student is following, if any. */
  followedLine?: string | null;
  hideSolution?: boolean;
  retrievalCue?: string;
}): TutorHintStep[] {
  const repertoire = teachingMoveFor(ctx.node);
  if (repertoire?.hintSteps && repertoire.hintSteps.length >= 5) {
    return HINT_STEP_IDS.map((id, index) => ({
      id,
      label: HINT_STEP_LABELS[id],
      text: repertoire.hintSteps[index] || repertoire.hintSteps[repertoire.hintSteps.length - 1],
    }));
  }

  const moveNumber = Math.ceil(ctx.ply / 2);
  const expectedBookSan = repertoire?.san || ctx.bookSan;
  const reviewed =
    moveNumber >= 10
      ? reviewedContinuationAtPosition(
          ctx.course?.openingId,
          ctx.chess.fen(),
          null,
          ctx.followedLine
        )
      : null;
  const deepBook =
    (expectedBookSan
      ? bookCoachingAtPosition(ctx.course?.openingId, ctx.chess.fen(), expectedBookSan)
      : bookCoachingAtPosition(ctx.course?.openingId, ctx.chess.fen())) || reviewed?.note;
  if (deepBook) {
    return [
      { id: "goal", label: HINT_STEP_LABELS.goal, text: deepBook.goal },
      { id: "piece", label: HINT_STEP_LABELS.piece, text: deepBook.piece },
      {
        id: "candidates",
        label: HINT_STEP_LABELS.candidates,
        text: deepBook.options?.length
          ? `This course recommends ${deepBook.san}. Other sound moves here are ${deepBook.options.map((option) => `${option.san} (${option.name})`).join(" and ")}. ${deepBook.candidates}`
          : deepBook.candidates,
      },
      {
        id: "move",
        label: HINT_STEP_LABELS.move,
        text: `The ${reviewed && deepBook === reviewed.note ? "illustrated continuation" : "course move"} is **${deepBook.san}**.`,
      },
      {
        id: "explanation",
        label: HINT_STEP_LABELS.explanation,
        text: [deepBook.why, deepBook.depth?.strategy, deepBook.depth?.tradeoff, deepBook.next]
          .filter(Boolean)
          .join(" "),
      },
    ];
  }

  const raw = buildHintLadder({
    ply: ctx.ply,
    opening: ctx.opening,
    chess: ctx.chess,
    engineBestSan: ctx.engineBestSan ?? null,
    bookSan: Object.prototype.hasOwnProperty.call(ctx, "bookSan") ? ctx.bookSan : undefined,
    candidateSans: ctx.candidateSans,
  });

  const goal = raw[0] || "Follow the phase goal: center, development, then king safety.";
  const piece = raw[1] || "Look at the piece that is doing the least work.";
  const candidates = raw[2] || "Compare two or three legal moves before you choose.";
  const { move, explanation } = splitMoveAndWhy(
    raw[3] || "Make your best guess — the coach will tell you what the move does."
  );
  // Without a course move here (off the course, or past the end of a line),
  // the hints come from the board, never from the course-wide objective.
  const boundary = !expectedBookSan
    ? positionGuideFor(ctx.course?.openingId, moveNumber, ctx.chess)
    : null;
  if (boundary) {
    const engineSan =
      ctx.engineBestSan && ctx.chess.moves().includes(ctx.engineBestSan) ? ctx.engineBestSan : null;
    const comparison = boundary.candidates.map((c) => `**${c.san}** — ${c.why}`).join("; ");
    return [
      { id: "goal", label: HINT_STEP_LABELS.goal, text: boundary.goal },
      { id: "piece", label: HINT_STEP_LABELS.piece, text: boundary.piece },
      {
        id: "candidates",
        label: HINT_STEP_LABELS.candidates,
        text: comparison
          ? `Compare these legal candidates: ${comparison}. Calculate the replies; these are ideas to examine, not engine rankings.`
          : boundary.next,
      },
      {
        id: "move",
        label: HINT_STEP_LABELS.move,
        text: engineSan
          ? `The engine's candidate is **${engineSan}**. Check how it addresses this position's priority.`
          : "There is no authored course move for this position. Choose between the legal candidates after comparing the opponent's replies.",
      },
      {
        id: "explanation",
        label: HINT_STEP_LABELS.explanation,
        text: `${boundary.next} ${boundary.watch}`,
      },
    ];
  }

  return [
    {
      id: "goal",
      label: HINT_STEP_LABELS.goal,
      text: authoredObjective(ctx.node) || goal,
    },
    { id: "piece", label: HINT_STEP_LABELS.piece, text: piece },
    {
      id: "candidates",
      label: HINT_STEP_LABELS.candidates,
      text: candidates,
    },
    { id: "move", label: HINT_STEP_LABELS.move, text: move },
    {
      id: "explanation",
      label: HINT_STEP_LABELS.explanation,
      text: repertoire?.explanation || explanation,
    },
  ];
}

/** The goal cue is authored for retrieval; deeper help declares what it exposes. */
export function buildTutorHintLadder(
  ctx: Parameters<typeof rawTutorHintLadder>[0]
): TutorHintStep[] {
  const hints = rawTutorHintLadder(ctx);
  if (!ctx.hideSolution) return hints;
  const expected = teachingMoveFor(ctx.node)?.san || ctx.bookSan;
  const bare = expected?.replace(/[+#?!]/g, "") || "";
  const escaped = bare.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const names = escaped ? new RegExp(`(?:^|[^a-zA-Z0-9])${escaped}(?:$|[^a-zA-Z0-9])`) : null;
  const moved = expected
    ? ctx.chess.moves({ verbose: true }).find((move) => move.san === expected)
    : null;
  return hints.map((hint, index) => {
    const text =
      index === 0
        ? ctx.retrievalCue ||
          textWithoutSan(hint.text, expected) ||
          "Look at what the opponent changed. Compare central tension, development and king safety."
        : hint.text;
    const destination =
      moved && new RegExp(`\\b(?:to|on|at)\\s+(?:the\\s+)?${moved.to}\\b`, "i").test(text);
    return {
      ...hint,
      text,
      revealsAnswer: index >= 3 || Boolean(names?.test(text)) || Boolean(destination),
    };
  });
}

export function masteryFromHintDepth(depth: HintDepth): "independent" | "assisted" {
  return depth === 0 ? "independent" : "assisted";
}

export function hintCreditLabel(depth: HintDepth): string {
  if (depth === 0) return "Independent";
  const id = HINT_STEP_IDS[depth - 1];
  return `Assisted · ${HINT_STEP_LABELS[id]}`;
}
